import {ApplicationsBadge} from '@/components/_domain/SidebarItemBadges/ApplicationsBadge/ApplicationsBadge';
import {NeedsResponseBadge} from '@/components/_domain/SidebarItemBadges/ConversationsBadge/ConversationsBadge';
import {PcfraOutstandingBadge} from '@/components/_domain/SidebarItemBadges/PcfraOutstandingBadge/PcfraOutstandingBadge';

import {SidebarItem} from '@/contexts/SidebarContext/SidebarContext';
import {
  BaseSidebarContentProps,
  SidebarCategory,
  SidebarCategoryItemProps,
} from '@/contexts/SidebarContext/types';
import {useUserTypeContext} from '@/contexts/UserTypeContext/UserTypeContext';
import {useGetClientPaymentsEnabledQuery} from '@/generated/graphql';
import {useFeatures} from '@/hooks/useFeatures/useFeatures';
import {SidebarContent} from '@/layout/Sidebar/SidebarContent';
import {
  BoltIcon,
  EventIcon,
  FramePersonIcon,
  HomeIcon,
  InboxIcon,
  GroupsIcon as MaterialGroupsIcon,
  RoomServiceIcon,
  RuleSettingsIcon,
  VillaIcon,
} from '@/material-symbols/icons';
import {useUserCommunitySettings} from '@ark/react';
import {
  AccountCircle,
  DonutLarge,
  ApartmentRounded,
  BookmarkBorderOutlined,
  Checklist,
  CreditCard,
  Dashboard,
  Draw,
  Engineering,
  FactCheck,
  Gavel,
  HealthAndSafety,
  Insights,
  Inventory,
  Key,
  MonitorHeart,
  PeopleAlt,
  RepeatRounded,
  Settings,
  TaskAlt,
  ViewCompact,
} from '@mui/icons-material';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import GroupsIcon from '@mui/icons-material/Groups';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import {DateTime} from 'luxon';
import {useMemo} from 'react';
import {
  appointmentsItem,
  dividerItem,
  documentsItem,
  eventsItem,
  facilitiesItem,
  formsItem,
  marketplaceItem,
  messagesItem,
  newsFeedItem,
  paymentsItem as paymentsAccountItem,
  productsItem,
  surveysItem,
} from '../sidebarItems';
import {useConversationSidebarItems} from '../useConversationSidebarItems';
import {SavedViewsCount} from '@/pages/ENT/Reporting/SavedViews/SavedViewsCount';

type ManagerSidebarContentProps = BaseSidebarContentProps & {
  isPinned: boolean;
};

const baseAdminItems: SidebarItem[] = [
  {
    url: '/teams',
    label: 'Teams',
    icon: <GroupsIcon fontSize="small" />,
  },
  {
    url: '/members',
    label: 'Team Members',
    icon: <AccountBoxIcon fontSize="small" />,
  },
  {
    url: '/tags',
    label: 'Tags',
    icon: <LocalOfferIcon fontSize="small" />,
  },
];

export const useAdminItems = () => {
  const [features] = useFeatures();

  return useMemo(() => {
    const adminItems: SidebarItem[] = [...baseAdminItems];
    if (features.Rules) {
      adminItems.push({
        url: '/rules',
        label: 'Rules',
        icon: <RuleSettingsIcon opticalSize={20} />,
      });
    }
    return adminItems;
  }, [features.Rules]);
};

const REPORTING_RELEASE_DATE = DateTime.fromISO('2026-11-02').minus({
  weeks: 2,
});

export const useReportingItems = () => {
  const [features] = useFeatures();

  return useMemo(() => {
    if (!features.Reporting) return [];
    const reportingItems: SidebarItem[] = [];
    if (features.PortfolioReporting) {
      reportingItems.push({
        url: '/reporting/portfolio',
        label: 'Portfolio',
        icon: <DonutLarge fontSize="small" />,
        releaseDate: REPORTING_RELEASE_DATE,
      });
    }
    reportingItems.push({
      url: '/reporting/site',
      label: 'Sites',
      icon: <VillaIcon opticalSize={20} />,
      releaseDate: REPORTING_RELEASE_DATE,
    });
    if (features.TeamReporting) {
      reportingItems.push({
        url: '/reporting/team',
        label: 'Team Performance',
        icon: <MaterialGroupsIcon opticalSize={20} />,
        releaseDate: REPORTING_RELEASE_DATE,
      });
    }
    reportingItems.push(dividerItem, {
      url: '/reporting/saved',
      label: 'Saved Views',
      icon: <BookmarkBorderOutlined fontSize="small" />,
      CountComponent: SavedViewsCount,
      opensRoot: true,
    });
    return reportingItems;
  }, [features.Reporting, features.PortfolioReporting, features.TeamReporting]);
};

export const useHasSafetyCenterAccess = () => {
  const {mergedSettings, loading} = useUserCommunitySettings([
    'manager',
    'concierge',
  ]);

  return {
    hasAccess: Boolean(mergedSettings?.safetyCenter),
    loading,
  };
};

export const useManagerCategoryItems = () => {
  const [features] = useFeatures();
  const {data} = useGetClientPaymentsEnabledQuery({
    fetchPolicy: 'cache-and-network',
  });
  const {mergedSettings, loading} = useUserCommunitySettings([
    'manager',
    'concierge',
  ]);
  const {hasAccess: hasSafetyCenterAccess} = useHasSafetyCenterAccess();
  const paymentsEnabled = data?.getClient?.paymentsEnabled;
  const adminItems = useAdminItems();
  const reportingItems = useReportingItems();
  const conversationsItems = useConversationSidebarItems({
    includeInactive: Boolean(features.InactiveCleanup),
  });

  const {hasBlocks, hasBlockSuggestions} = useUserTypeContext();

  return useMemo(() => {
    if (loading) {
      return new Map<SidebarCategory, SidebarItem[]>();
    }

    const categoryItemsMap = new Map<SidebarCategory, SidebarItem[]>();

    const dashboardItems: SidebarItem[] = [
      {
        url: '/dashboard',
        label: 'Dashboard',
        icon: <Dashboard fontSize="small" />,
      },
    ];
    categoryItemsMap.set('dashboard', dashboardItems);

    if (reportingItems.length > 0) {
      categoryItemsMap.set('reporting', reportingItems);
    }

    const communitiesItems: SidebarItem[] = [
      {
        url: '/communities',
        label: 'Overview',
        icon: <ApartmentRounded fontSize="small" />,
      },
      {
        url: '/homes',
        label: 'Homes',
        icon: <HomeIcon opticalSize={20} />,
      },
      {
        url: '/groups',
        label: 'Groups',
        icon: <MaterialGroupsIcon opticalSize={20} />,
      },
      {
        url: '/pulse',
        label: 'Pulse',
        icon: <MonitorHeart fontSize="small" />,
        releaseDate: DateTime.fromISO('2026-01-19'),
      },
      dividerItem,
      {
        url: '/contact-profiles',
        label: 'Contacts',
        icon: <AccountCircle fontSize="small" />,
      },
      {
        url: '/verification',
        label: 'Applicants',
        icon: <FramePersonIcon opticalSize={20} />,
        BadgeComponent: ApplicationsBadge,
      },
    ];
    if (features.Contractors) {
      communitiesItems.push({
        url: '/contractors',
        label: 'Contractors',
        icon: <Engineering fontSize="small" />,
      });
    }

    if (features.AutomatedBlocks && (hasBlocks || hasBlockSuggestions)) {
      communitiesItems.push({
        url: '/blocks',
        label: 'Blocks',
        icon: <ViewCompact fontSize="small" />,
      });
    }

    categoryItemsMap.set('communities', communitiesItems);

    if (hasSafetyCenterAccess) {
      const safetyCenterItems: SidebarItem[] = [
        {
          url: '/safety-center/overview',
          label: 'Overview',
          icon: <Insights fontSize="small" />,
        },
        {
          url: '/safety-center/records',
          label: 'Occupants',
          icon: <PeopleAlt fontSize="small" />,
          BadgeComponent: PcfraOutstandingBadge,
        },
      ];
      if (features.TaskChecklists) {
        safetyCenterItems.push({
          url: '/safety-center/inspections',
          label: 'Inspections',
          icon: <FactCheck fontSize="small" />,
        });
      }
      categoryItemsMap.set('safetyCenter', safetyCenterItems);
    }

    if (features.Tasks) {
      const tasksItems: SidebarItem[] = [
        {
          url: '/tasks',
          label: 'Tasks',
          icon: <TaskAlt fontSize="small" />,
        },
        {
          url: '/tasks/scheduled',
          label: 'Scheduled Tasks',
          icon: <RepeatRounded fontSize="small" />,
        },
      ];
      if (features.TaskChecklists) {
        tasksItems.push({
          url: '/tasks/templates',
          label: 'Templates',
          icon: <Checklist fontSize="small" />,
        });
      }
      categoryItemsMap.set('tasks', tasksItems);
    }

    const engagementItems: SidebarItem[] = [];
    if (mergedSettings?.newsfeed) {
      engagementItems.push(newsFeedItem);
    }
    if (mergedSettings?.surveys) {
      engagementItems.push(surveysItem);
    }
    if (mergedSettings?.documents) {
      engagementItems.push(documentsItem);
    }
    if (mergedSettings?.forms) {
      engagementItems.push(formsItem);
    }
    if (mergedSettings?.marketplace) {
      engagementItems.push(marketplaceItem);
    }

    if (mergedSettings?.messages) {
      if (features.ConversationLayout) {
        categoryItemsMap.set('conversations', conversationsItems);
      } else {
        engagementItems.push(messagesItem);
      }
    }

    categoryItemsMap.set('engagement', engagementItems);

    const bookingsItems: SidebarItem[] = [];
    if (mergedSettings?.events) {
      bookingsItems.push(eventsItem);
    }
    if (mergedSettings?.facilities) {
      bookingsItems.push(facilitiesItem);
    }
    if (mergedSettings?.appointments) {
      bookingsItems.push(appointmentsItem);
    }
    categoryItemsMap.set('bookings', bookingsItems);

    const conciergeItems: SidebarItem[] = [
      {
        url: '/packages',
        label: 'Packages',
        icon: <Inventory fontSize="small" />,
      },
      {
        url: '/keys',
        label: 'Keys',
        icon: <Key fontSize="small" />,
      },
      {
        url: '/visitors',
        label: 'Visitors',
        icon: <Draw fontSize="small" />,
      },
    ];
    categoryItemsMap.set('concierge', conciergeItems);

    const onboardingItems: SidebarItem[] = [];

    categoryItemsMap.set('onboarding', onboardingItems);

    if (mergedSettings?.compliance) {
      const complianceItems: SidebarItem[] = [
        {
          url: '/compliance/blocks',
          label: 'Blocks',
          icon: <ViewCompact fontSize="small" />,
        },
        {
          url: '/compliance/requirements',
          label: 'Requirements',
          icon: <Checklist fontSize="small" />,
        },
      ];
      categoryItemsMap.set('compliance', complianceItems);
    }

    const hasPaymentsProducts =
      features.CommunityProducts && paymentsEnabled && mergedSettings?.products;
    const hasPaymentsAccounts =
      features.LeaseholderPayments && mergedSettings?.accounts;

    if (hasPaymentsProducts || hasPaymentsAccounts) {
      const paymentsItems: SidebarItem[] = [];
      if (hasPaymentsProducts) {
        paymentsItems.push(productsItem);
      }
      if (hasPaymentsAccounts) {
        paymentsItems.push(paymentsAccountItem);
      }
      categoryItemsMap.set('payments', paymentsItems);
    }

    categoryItemsMap.set('admin', adminItems);

    return categoryItemsMap;
  }, [
    features,
    paymentsEnabled,
    mergedSettings,
    loading,
    adminItems,
    reportingItems,
    conversationsItems,
    hasBlocks,
    hasBlockSuggestions,
    hasSafetyCenterAccess,
  ]);
};

export const ManagerSidebarContent = (props: ManagerSidebarContentProps) => {
  const [features] = useFeatures();
  const {data} = useGetClientPaymentsEnabledQuery({
    fetchPolicy: 'cache-and-network',
  });
  const {mergedSettings, loading} = useUserCommunitySettings([
    'manager',
    'concierge',
  ]);
  const {hasAccess: hasSafetyCenterAccess} = useHasSafetyCenterAccess();
  const paymentsEnabled = data?.getClient?.paymentsEnabled;

  const {clientAdmin} = useUserTypeContext();
  const isAdmin = Boolean(clientAdmin);

  if (loading) {
    return null;
  }

  const engagementCategoryItem: SidebarCategoryItemProps = {
    label: 'engagement',
    icon: <BoltIcon opticalSize={20} />,
  };

  const conversationsCategoryItem: SidebarCategoryItemProps = {
    label: 'conversations',
    icon: <InboxIcon opticalSize={20} />,
    url: '/messages',
    BadgeComponent: NeedsResponseBadge,
  };

  const bookingsCategoryItem: SidebarCategoryItemProps = {
    label: 'bookings',
    icon: <EventIcon opticalSize={20} />,
  };

  const communitiesCategoryItem: SidebarCategoryItemProps = {
    label: 'communities',
    icon: <VillaIcon opticalSize={20} />,
    BadgeComponent: ApplicationsBadge,
  };

  const safetyCenterCategoryItem: SidebarCategoryItemProps = {
    label: 'safetyCenter',
    icon: <HealthAndSafety fontSize="small" />,
    url: '/safety-center/records',
    BadgeComponent: PcfraOutstandingBadge,
  };

  const tasksCategoryItem: SidebarCategoryItemProps = {
    label: 'tasks',
    icon: <TaskAlt fontSize="small" />,
    url: '/tasks',
  };

  const complianceCategoryItem: SidebarCategoryItemProps = {
    label: 'compliance',
    icon: <Gavel fontSize="small" />,
  };

  const adminCategoryItem: SidebarCategoryItemProps = {
    label: 'admin',
    icon: <Settings fontSize="small" />,
  };

  const categoryItems: SidebarCategoryItemProps[] = [
    {
      label: 'dashboard',
      icon: <Dashboard fontSize="small" />,
    },
  ];

  if (features.Reporting) {
    categoryItems.push({
      label: 'reporting',
      icon: <Insights fontSize="small" />,
    });
  }

  categoryItems.push(communitiesCategoryItem);

  if (mergedSettings?.messages && features.ConversationLayout) {
    categoryItems.push(conversationsCategoryItem);
  }

  categoryItems.push(engagementCategoryItem, bookingsCategoryItem);

  if (features.Tasks) {
    categoryItems.push(tasksCategoryItem);
  }

  if (hasSafetyCenterAccess) {
    categoryItems.push(safetyCenterCategoryItem);
  }

  if (mergedSettings?.compliance) {
    categoryItems.push(complianceCategoryItem);
  }

  categoryItems.push({
    label: 'concierge',
    icon: <RoomServiceIcon opticalSize={20} />,
  });

  const hasPaymentsProducts =
    features.CommunityProducts && paymentsEnabled && mergedSettings?.products;
  const hasPaymentsAccounts =
    features.LeaseholderPayments && mergedSettings?.accounts;

  if (hasPaymentsProducts || hasPaymentsAccounts) {
    const paymentsCategoryItem: SidebarCategoryItemProps = {
      label: 'payments',
      icon: <CreditCard fontSize="small" />,
    };
    categoryItems.push(paymentsCategoryItem);
  }

  if (isAdmin) {
    categoryItems.push(adminCategoryItem);
  }

  return (
    <SidebarContent
      items={categoryItems}
      onClose={props.onClose}
      isPinned={props.isPinned}
    >
      {props.children}
    </SidebarContent>
  );
};
